import { Trans, useTranslation } from 'react-i18next';
import React, { useMemo, useState, useCallback, ReactNode, useEffect } from 'react';
import cx from 'clsx';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';

import { IconRcTick } from '@/assets';
import { Button, L2PayModal, ModalStepProps, UserAvatar, UserSearchInput } from '@/components';
import { PAID_FEATURE_RECEIVER } from '@/consts';
import {
  useSubscribePaidFeature,
  useQueryPaidFeatureCanSubscribe,
  useQueryUserPaidList,
  useCurrentAddressInfo,
  useUserInfo,
  useCurrentAddressAccount,
} from '@/hooks';
import { useCurrentUserId, formatNum, getUserNameByUserInfo, formatDate, numFormat } from '@/utils';

import style from './index.module.scss';

export const PaidFeatureModal = ({
  featureItem,
  turnOff,
  sendOther,
  defaultTargetUserId,
}: {
  featureItem: PaidFeatureItem;
  turnOff: () => void;
  sendOther: boolean;
  defaultTargetUserId?: string;
}) => {
  const [selectedDuration, setSelectedDuration] = useState<number>(featureItem.durations[0]);
  const currentAddress = useCurrentUserId();
  const [sendTargetId, setSendTargetId] = useState<string>();
  const { mutateAsync } = useSubscribePaidFeature();
  const totalAmount = useMemo(
    () => selectedDuration * featureItem.price,
    [featureItem.price, selectedDuration],
  );
  const realTotalAmount = useMemo(() => totalAmount / 10000, [totalAmount]);

  const txParams = useMemo(
    () =>
      totalAmount && currentAddress
        ? {
            price: totalAmount,
            message: `Pay $${formatNum(
              realTotalAmount,
            )} for ${selectedDuration} month-subscription of feature: ${featureItem.name}`,
            senderAddr: currentAddress,
            receiverAddr: PAID_FEATURE_RECEIVER,
          }
        : undefined,
    [currentAddress, featureItem.name, realTotalAmount, selectedDuration, totalAmount],
  );

  const payTitle = useMemo(
    () => (
      <div className={style.payTitle}>
        <Trans
          i18nKey="PaidFeatureModal.1pqg"
          values={{
            price: `$${formatNum(realTotalAmount)}`,
            feature: featureItem.name,
            selectedDuration,
          }}>
          Pay<span className={style.payTitleWeight}>price</span> for {selectedDuration}{' '}
          month-subscription of feature: {featureItem.name}
        </Trans>
      </div>
    ),
    [featureItem.name, realTotalAmount, selectedDuration],
  );

  const handlePaySubmit = useCallback(
    async ({ tx }) => {
      mutateAsync({
        tx,
        id: featureItem.id,
        duration: selectedDuration,
        sendToUserId: sendOther ? sendTargetId : undefined,
      });
    },
    [featureItem.id, mutateAsync, selectedDuration, sendOther, sendTargetId],
  );

  return (
    <L2PayModal
      turnOff={turnOff}
      txParams={txParams}
      payTitle={payTitle}
      modalClassName={style.modalClassName}
      onPaySubmit={handlePaySubmit}>
      <SubscribePanel
        featureItem={featureItem}
        setDuration={setSelectedDuration}
        sendOther={sendOther}
        setSendTargetId={setSendTargetId}
        key={'subscribe-panel'}
        defaultTargetUserId={defaultTargetUserId}
      />
    </L2PayModal>
  );
};

const SubscribePanel = ({
  showNext,
  setDuration,
  setSendTargetId,
  featureItem,
  sendOther,
  onClose,
  defaultTargetUserId,
}: ModalStepProps<null> & {
  setDuration: (_: number) => void;
  featureItem: PaidFeatureItem;
  sendOther: boolean;
  setSendTargetId: (_?: string) => void;
  defaultTargetUserId?: string;
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const [otherUser, setOtherUser] = useState<UserInfo>();
  const { data: myAccountInfo, isLoading } = useCurrentAddressAccount();
  const { data: defaultUserInfo } = useUserInfo(defaultTargetUserId);

  const currentUserId = useCurrentUserId();
  const { userInfo: currentUserInfo } = useCurrentAddressInfo();

  const targetUserInfo = useMemo(
    () => (sendOther ? otherUser : currentUserInfo),
    [sendOther, otherUser, currentUserInfo],
  );

  useEffect(() => {
    sendOther && setSendTargetId(otherUser?.id);
  });

  const [errorMsg, setErrorMsg] = useState<ReactNode>(null);
  const [selectedDuration, setSelectedDuration] = useState<number>(featureItem.durations[0]);
  const { data: canSubscribeData, isLoading: canSubscribeDataLoading } =
    useQueryPaidFeatureCanSubscribe(featureItem.id, selectedDuration, targetUserInfo?.id);
  const { data: subscriptionsList } = useQueryUserPaidList(
    sendOther ? otherUser?.id : currentUserId,
    Boolean(sendOther ? otherUser : currentUserId),
  );
  const sendOtherEmptyStatus = useMemo(() => sendOther && !otherUser, [sendOther, otherUser]);

  const subscribedItem = useMemo(
    () => subscriptionsList?.find(item => item.id === featureItem.id),
    [featureItem.id, subscriptionsList],
  );

  const expectedValidPeriod = useMemo(() => {
    const currentPeriod = subscribedItem ? subscribedItem?.expired_at * 1000 : Date.now();
    return dayjs(currentPeriod).add(selectedDuration, 'month').format('YYYY/MM/DD');
  }, [selectedDuration, subscribedItem]);

  const totalAmount = useMemo(
    () => selectedDuration * featureItem.price,
    [featureItem.price, selectedDuration],
  );

  const realTotalAmount = useMemo(() => totalAmount / 10000, [totalAmount]);

  const insufficientBalance = useMemo(
    () => !myAccountInfo?.balance || realTotalAmount > myAccountInfo?.balance,
    [myAccountInfo?.balance, realTotalAmount],
  );

  const isValid = useMemo(() => {
    return (
      isLoading ||
      canSubscribeDataLoading ||
      (!insufficientBalance && canSubscribeData?.can_subscribe)
    );
  }, [canSubscribeData?.can_subscribe, canSubscribeDataLoading, isLoading, insufficientBalance]);

  useEffect(() => {
    if (sendOtherEmptyStatus) {
      setErrorMsg('');
    } else if (!canSubscribeData?.can_subscribe) {
      setErrorMsg(canSubscribeData?.msg);
    } else if (isValid) {
      setErrorMsg('');
    } else {
      setErrorMsg(`${t('L2PayModal.31j7iz')} ${numFormat(myAccountInfo?.balance, 2, '$')}`);
    }
  }, [
    canSubscribeData?.can_subscribe,
    canSubscribeData?.msg,
    isValid,
    sendOtherEmptyStatus,
    myAccountInfo?.balance,
    t,
  ]);

  const subscribedInfoText = useMemo(() => {
    if (subscribedItem) {
      return sendOther
        ? t('PaidFeatureModal.additional')
        : t('PaidFeatureModal.expiredAt', {
            date: formatDate(subscribedItem?.expired_at * 1000, 'YYYY/MM/DD'),
          });
    } else {
      return sendOther ? '' : t('PaidFeatureModal.notSubscribed');
    }
  }, [sendOther, subscribedItem, t]);

  const handleSubmit = useCallback(() => {
    if (!isValid) {
      return;
    }

    setErrorMsg('');

    setDuration(selectedDuration);
    showNext?.();
  }, [isValid, setDuration, selectedDuration, showNext]);

  return (
    <div>
      <div className={style.paidFeatureModal}>
        <div className={style.paidFeatureModalTitle}>
          {sendOther
            ? t('PaidFeatureModal.sendGift', { name: featureItem.name })
            : featureItem.name}
        </div>
        <div className={style.paidFeatureModalContent}>
          <div className={style.currentSubscribeInfo}>
            {sendOther ? (
              <div className={style.inputWrapper}>
                <UserSearchInput
                  onChange={setOtherUser}
                  defaultValue={defaultUserInfo}
                  excludeSelf
                />
              </div>
            ) : (
              <div className={style.userInfo}>
                <UserAvatar size={28} data={targetUserInfo?.desc} />
                <div> {getUserNameByUserInfo(targetUserInfo, true)}</div>
              </div>
            )}
            <div>
              {sendOtherEmptyStatus || !subscribedInfoText ? null : (
                <div className={cx(style.tip)}>{subscribedInfoText}</div>
              )}
            </div>
          </div>

          <div className={style.list}>
            {featureItem.durations.map(month => (
              <div
                className={cx(style.listItem, month === selectedDuration && style.selected)}
                key={month}
                onClick={() => setSelectedDuration(month)}>
                <div className={style.main}>
                  ${formatNum((month * featureItem.price) / 10000, 2)}
                </div>
                <div>{t('PaidFeatureModal.1pysn4', { count: month })}</div>
                <IconRcTick className={style.selectedIcon} />
              </div>
            ))}
          </div>
        </div>
        {errorMsg ? (
          <div className={style.depositErrMsg}>{errorMsg}</div>
        ) : (
          <div className={style.expectedValidPeriod}>
            {canSubscribeDataLoading || sendOtherEmptyStatus ? null : (
              <>
                {t('PaidFeatureModal.validFrom', {
                  date: expectedValidPeriod,
                })}
              </>
            )}
          </div>
        )}

        <div className={style.paidFeatureModalFooter}>
          <Button type="default" className={style.footerButton} onClick={onClose}>
            {t('PaidFeatureModal.x9d2fu')}
          </Button>
          {insufficientBalance ? (
            <Button
              type="primary"
              className={style.footerButton}
              onClick={() => history.push('/account')}>
              {t('L2PayModal.u5swep')}
            </Button>
          ) : (
            <Button
              type="primary"
              disabled={isLoading || !isValid}
              className={style.footerButton}
              onClick={handleSubmit}>
              {t('PaidFeatureModal.179d1nk')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
